import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import FocusLock from "react-focus-lock";
import { getInitials } from "@wrstudios/utils";
import { UserAvatar } from "@wrstudios/components";
import { useSession } from "../../../Session";
import { useLive } from "../LiveProvider";
import IconEmail from "../icons/IconEmail";
import {
  Container,
  Button,
  Details,
  Name,
  Company,
  AvatarContainer,
  Menu,
  MenuSection,
  MobileMenuSection,
  Link,
  Email
} from "./styled/live-agent";

function LiveAgent() {
  const containerRef = useRef();
  const { pathname } = useLocation();
  const { guid } = useParams();
  const { config } = useSession();
  const { agent } = useLive();
  const [isOpen, setIsOpen] = useState(false);
  const isSplash = pathname === `/live/${guid}`;

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        e.target !== containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  return (
    <Container
      ref={containerRef}
      className={`agent ${isSplash ? "agent-is-splash" : ""}`}>
      <Button onClick={() => setIsOpen(!isOpen)}>
        <Details>
          <Name isSplash={isSplash} className="agent-name">
            {agent.name}
          </Name>
          <Company isSplash={isSplash} className="agent-company">
            {agent.companyName}
          </Company>
        </Details>
        <AvatarContainer
          isSplash={isSplash}
          hasAvatarUrl={!!agent.avatarUrl}
          className="agent-avatar-container">
          <UserAvatar
            initials={getInitials(agent.name)}
            avatarUrl={agent.avatarUrl}
          />
        </AvatarContainer>
      </Button>
      {isOpen && (
        <Menu onKeyDown={handleKeyDown}>
          <FocusLock returnFocus>
            {(!!agent.name || !!agent.companyName) && (
              <MobileMenuSection>
                <div>{agent.name}</div>
                <div>{agent.companyName}</div>
              </MobileMenuSection>
            )}
            {!!agent.officeAddress && (
              <MenuSection>{agent.officeAddress}</MenuSection>
            )}
            {(!!agent.officePhone || !!agent.mobilePhone || !!agent.email) && (
              <MenuSection className="agent-menu-section">
                <table>
                  <tbody>
                    {!!agent.officePhone && (
                      <tr>
                        <td>Office:</td>
                        <td>
                          <Link
                            href={`tel:${agent.officePhone}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            {agent.officePhone}
                          </Link>
                        </td>
                      </tr>
                    )}
                    {!!agent.mobilePhone && (
                      <tr>
                        <td>Cell:</td>
                        <td>
                          <Link
                            href={`tel:${agent.mobilePhone}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            {agent.mobilePhone}
                          </Link>
                        </td>
                      </tr>
                    )}
                    {!!agent.email && (
                      <tr>
                        <td>Email:</td>
                        <td>
                          <Link
                            href={`mailto:${agent.email}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            {agent.email}
                          </Link>
                        </td>
                      </tr>
                    )}
                    {!!agent.licenseNumber && !!agent.licenseLabel && (
                      <tr>
                        <td>{agent.licenseLabel}:</td>
                        <td>{agent.licenseNumber}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MenuSection>
            )}
            <MenuSection className="agent-menu-section agent-menu-section-email">
              <Email
                as="a"
                href={`mailto:?subject=Live Report&body=Here's your live report! ${config.url}/live/${guid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="agent-menu-contact">
                <IconEmail />
                Email Report
              </Email>
            </MenuSection>
          </FocusLock>
        </Menu>
      )}
    </Container>
  );
}

export default LiveAgent;
